// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { classifyPortfolio } from '@advisor-app/features/utils/classify-portfolio';
import {
  selectWorkflowAsset,
  setWorkflow,
} from '@advisor-app/store/slices/workflow';
import { useAppSelector } from '@advisor-app/store/store';
import { Button } from '@mui/material';
import { type PortfolioMarketPlaceResponseBody as PortfolioDetails } from '@shared/data-access/interfaces/api/engine/portfolio-types';
import { type BasePortfolio } from '@shared/data-access/interfaces/common/base-portfolio';
import { Heading, useDynamicDialog } from '@shared/ui-kit/components';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

export const useAnalyzeActions = (portfolio: PortfolioDetails) => {
  const { t } = useTranslation();
  const workflowAsset = useAppSelector(selectWorkflowAsset);
  const [open, setOpen] = useState(false);

  const dispatch = useDispatch();
  const { openDialog, closeDialog } = useDynamicDialog();

  const handleClose = useCallback(() => void setOpen(false), []);

  const showAlert = useCallback(() => {
    const content = (
      <div className="text-center">
        <Heading
          variant="h2"
          title={t('glossary.portfolio-analysis', { ns: 'glossary' })}
        />
        <p className="mb-10 mt-5 text-subtitle">
          {t('marketplace.unsupported-assets-message', { ns: 'components' })}
        </p>
        <Button className="!rounded-[4px] !py-1" onClick={closeDialog}>
          {t('common.ok', { ns: 'common' })}
        </Button>
      </div>
    );
    openDialog(content, {
      title: t('glossary.portfolio-analysis', { ns: 'glossary' }),
      showCloseButton: false,
      maxWidth: 'xs',
    });
  }, [closeDialog, openDialog, t]);

  const handleAnalyze = useCallback(async () => {
    try {
      const tickers = portfolio.holdings.map(({ ticker }) => ticker);
      const assetType = await classifyPortfolio(tickers);
      if (assetType === 'other') {
        showAlert();
        return;
      }
      dispatch(setWorkflow({ type: 'research', asset: assetType }));
      setOpen(true);
    } catch (e: any) {
      console.error(e);
    }
  }, [dispatch, portfolio.holdings, showAlert]);

  const dataPortfolio = useMemo(() => {
    const { holdings } = portfolio;

    const portfolioData: BasePortfolio = {
      date: [],
      ticker: [],
      weight: [],
      unrealized_capital_gains: [],
    };

    holdings.forEach(asset => {
      const weight = asset.weight;
      portfolioData.ticker.push(asset.ticker);
      portfolioData.weight.push(weight);
      portfolioData.unrealized_capital_gains.push(asset.unrealized_capital_gains);
    });

    return portfolioData;
  }, [portfolio]);

  return {
    open,
    handleClose,
    handleAnalyze,
    dataPortfolio,
    workflowAsset,
  };
};
